import { ReactElement, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import messages from 'src/apps/instana-roi/locales/messages';
import { Reset16, PdfReference16, Calendar16 } from '@carbon/icons-react';
import './BVAResults.scss';
import {
  Button,
  ButtonSet,
  InlineLoading,
  InlineLoadingStatus,
} from 'carbon-components-react';
import { LineChart } from '@carbon/charts-react';
import {
  YEARLY,
  OpenSourceTechnologies,
  emailCOSJson,
} from 'src/apps/instana-roi/constants/defaults';
import {
  PREVENTION_ASSUMPTION,
  SPEED_GAIN_ASSUMPTION,
} from 'src/apps/instana-roi/constants/prices';
import { segmentsTracking } from 'src/common/lib/tracker';
import { pageUrl, productTitle } from 'src/apps/instana-roi/lib/tracker';
import { toJpeg } from 'html-to-image';
import { IBMLocale } from 'src/common/locale/mapValidLocale';

import Feedback from 'src/common/utils/Feedback';
import { useTimer } from 'src/common/hooks/useTimer';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { DocumentPdf32 } from '@carbon/icons-react';
import { PDFBVAResultsProps, PDFGenerate } from './PDFGenerate';
import { useIntl } from 'react-intl';
import { initUrxForm } from 'src/common/hooks/widgets/urxForm';
import * as URX_FORM from 'src/apps/instana-roi/constants/urx-form';

export interface BVAResultsProps {
  lang: string;
  disruptions: number;
  preventions: number;
  speedGain: number;
  avgTime: string;
  prometheus: boolean;
  openTracing: boolean;
  openTelemetry: boolean;
  jaeger: boolean;
  zipkin: boolean;
  openCensus: boolean;
  setSpeedGain: React.Dispatch<React.SetStateAction<number>>;
  setPrevention: React.Dispatch<React.SetStateAction<number>>;
  itOpsEmployees: number;
  developers: number;
  setViewBVAResults: React.Dispatch<React.SetStateAction<boolean>>;
  reset: () => void;
  openSourceTechnoList: string;
  setOpenSourceTechnoList: React.Dispatch<React.SetStateAction<string>>;
  developerEffiencyGains: string;
  itOpsEffiencyGains: string;
  openSourceGains: string;
  totalEffiencyGains: string;
  tenThousandDollarPerHour: number;
  thirtyThousandDollarPerHour: number;
  fiftyThousandDollarPerHour: number;
  twoHunderedThousandDollarPerHour: number;
  drawerOpen: boolean;
  setDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  bpName: string;
  setShowPDF: React.Dispatch<React.SetStateAction<boolean>>;
  setResultsImage: React.Dispatch<React.SetStateAction<string>>;
  setSummaryImage: React.Dispatch<React.SetStateAction<string>>;
  showPDF: boolean;
  urxlang: IBMLocale;
  companyId: string;
  environment: string;
  setPdfExport: React.Dispatch<React.SetStateAction<boolean>>;
  pdfExport: boolean;
  resultsImage?: string;
  summaryImage?: string;
  code: string | null;
}

// Function to show results of calculations
export default function BVAResults({
  disruptions,
  preventions,
  speedGain,
  avgTime,
  prometheus,
  openTracing,
  openTelemetry,
  jaeger,
  zipkin,
  openCensus,
  setSpeedGain,
  setPrevention,
  setViewBVAResults,
  reset,
  itOpsEmployees,
  developers,
  openSourceTechnoList,
  setOpenSourceTechnoList,
  developerEffiencyGains,
  itOpsEffiencyGains,
  openSourceGains,
  totalEffiencyGains,
  tenThousandDollarPerHour,
  thirtyThousandDollarPerHour,
  fiftyThousandDollarPerHour,
  twoHunderedThousandDollarPerHour,
  setShowPDF,
  setResultsImage,
  setSummaryImage,
  showPDF,
  setPdfExport,
  pdfExport,
  resultsImage,
  summaryImage,
  urxlang,
  environment,
  companyId,
  setDrawerOpen,
  code,
}: BVAResultsProps): ReactElement {
  const [pdfIcon, setPdfIcon] = useState(false);
  const [loaderStatus, setLoaderStaus] = useState(
    'active' as InlineLoadingStatus,
  );
  const [loaderDes, setLoaderDesc] = useState(
    <FormattedMessage {...messages.pdfGenerated} />,
  );
  const [fetchPriceStatus, setFetchPriceStatus] = useState('');
  const [fetchSummaryStatus, setFetchSummaryStatus] = useState('');
  const [conversionFired, setConversionFired] = useState(0);
  const fetchImagesFromHTMLElement = async (
    id: string,
    bgColor: string,
  ): Promise<string> => {
    const div = document.getElementById(id) as HTMLElement;
    return toJpeg(div, { backgroundColor: bgColor });
  };
  const intl = useIntl();
  const props: PDFBVAResultsProps = {
    imgResults: resultsImage ? resultsImage : '',
    imgSummary: summaryImage ? summaryImage : '',
    pdfPara1: intl.formatMessage(messages.pdfPara1),
    pdfPara2: intl.formatMessage(messages.pdfPara2),
    pdfPara3: intl.formatMessage(messages.pdfPara3),
    pdfPara4: intl.formatMessage(messages.pdfPara4),
    pdfPara5: intl.formatMessage(messages.pdfPara5),
    pdfPara6: intl.formatMessage(messages.pdfPara6),
    pdfPara7: intl.formatMessage(messages.pdfPara7),
    heading: intl.formatMessage(messages.page_heading_3),
  };

  const { timeout, init } = useTimer(false, 6000);

  const pdfObject = PDFGenerate(props);

  const bookAMeeting = () => {
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set('schedulerform', '');
    window.location.search = urlParams.toString();
    console.log(urlParams.toString());
  };

  useEffect(() => {
    if (timeout) {
      if (code === 'standalone') {
        initUrxForm(
          environment,
          URX_FORM.INSTANCE_ID,
          URX_FORM.FORM_ID,
          urxlang as IBMLocale,
          companyId,
        );
        setDrawerOpen(true);
      } else {
        localStorage.setItem('showInstanaForm', 'true');
      }
    }
  }, [timeout]);

  useEffect(() => {
    if (conversionFired === 1) {
      fetchImagesFromHTMLElement('pricing', '#fff')
        .then((dataUrl) => {
          setResultsImage(dataUrl);
          setFetchPriceStatus('success');
          setConversionFired(2);
        })
        .catch((error) => {
          console.error(
            'oops, something went wrong while fetching the images for results',
            error,
          );
          setConversionFired(3);
          setFetchPriceStatus('error');
        });

      fetchImagesFromHTMLElement('summary', '#f4f4f4')
        .then((dataUrl) => {
          setSummaryImage(dataUrl);
          setFetchSummaryStatus('success');
          setLoaderStaus('finished' as InlineLoadingStatus);
          setConversionFired(2);
        })
        .catch((error) => {
          console.error(
            'oops, something went wrong while fetching the images for summary',
            error,
          );
          setConversionFired(3);
          setFetchSummaryStatus('error');
        });
    }
  }, [conversionFired]);

  useEffect(() => {
    if (showPDF && conversionFired === 0) {
      setConversionFired(1);
    }
    if (conversionFired === 2) {
      setLoaderDesc(<FormattedMessage {...messages.pdfCompleted} />);
      setPdfIcon(true);
      setConversionFired(0);
      setFetchPriceStatus('');
      setFetchSummaryStatus('');
      setPdfExport(true);
      setShowPDF(false);
    } else if (conversionFired === 3) {
      setLoaderStaus('error' as InlineLoadingStatus);
      setConversionFired(0);
      setPdfIcon(true);
      setLoaderDesc(<FormattedMessage {...messages.pdfError} />);
      setShowPDF(false);
    }
  }, [
    pdfIcon,
    fetchPriceStatus,
    fetchSummaryStatus,
    setPdfExport,
    conversionFired,
    showPDF,
  ]);

  const calculatePreventions = (
    disruptions: number,
    setPrevention: React.Dispatch<React.SetStateAction<number>>,
  ) => {
    setPrevention(disruptions * YEARLY * PREVENTION_ASSUMPTION);
  };

  const calculateSpeedGains = (
    disruptions: number,
    setSpeedGain: React.Dispatch<React.SetStateAction<number>>,
  ) => {
    setSpeedGain(disruptions * YEARLY * SPEED_GAIN_ASSUMPTION);
  };

  const generateOpenSourceTechnologies = (
    prometheus: boolean,
    openTracing: boolean,
    openTelemetry: boolean,
    jaeger: boolean,
    zipkin: boolean,
    openCensus: boolean,
  ) => {
    const PROMETHUS = prometheus ? OpenSourceTechnologies.prometheus : '';
    const OPENTRACING = openTracing ? OpenSourceTechnologies.openTracing : '';
    const OPENTELEMETRY = openTelemetry
      ? OpenSourceTechnologies.openTelemetry
      : '';
    const JAEGER = jaeger ? OpenSourceTechnologies.jaeger : '';
    const ZIPKIN = zipkin ? OpenSourceTechnologies.zipkin : '';
    const OPENCENSUS = openCensus ? OpenSourceTechnologies.openCensus : '';
    setOpenSourceTechnoList(
      PROMETHUS + OPENTRACING + OPENTELEMETRY + JAEGER + ZIPKIN + OPENCENSUS,
    );
  };

  // Define the chart content
  const chart = {
    data: [
      {
        group: '',
        key: '$10k/hr',
        value: tenThousandDollarPerHour,
      },
      {
        group: '',
        key: '$30k/hr',
        value: thirtyThousandDollarPerHour,
      },
      {
        group: '',
        key: '$50k/hr',
        value: fiftyThousandDollarPerHour,
      },
      {
        group: '',
        key: '$200K/hr',
        value: twoHunderedThousandDollarPerHour,
      },
    ],
    options: {
      title: '',
      axes: {
        bottom: {
          title: 'Application Value',
          mapsTo: 'key',
          scaleType: 'labels',
        },
        left: {
          mapsTo: 'value',
          title: 'Potential revenue savings ($)',
          scaleType: 'linear',
        },
      },
      height: '400px',
      toolbar: {
        enabled: false,
      },
      curve: 'curveMonotoneX',
      data: {
        loading: false,
      },
    } as any,
  };

  // Perform calculations
  useEffect(() => {
    calculatePreventions(disruptions, setPrevention);
    calculateSpeedGains(disruptions, setSpeedGain);
    generateOpenSourceTechnologies(
      prometheus,
      openTracing,
      openTelemetry,
      jaeger,
      zipkin,
      openCensus,
    );
  }, [
    disruptions,
    prometheus,
    openTracing,
    openTelemetry,
    jaeger,
    zipkin,
    openCensus,
    developers,
    preventions,
    speedGain,
  ]);

  return (
    <div className="BVA_Results">
      <div className="bx--row mar3 padding">
        <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-4">
          <div className="bx--row">
            <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
              <p className="page_sub_heading">
                <FormattedMessage {...messages.page_heading_3} />
              </p>
            </div>
          </div>
          <div className="bx--row mt-3">
            <div className="bx--col-lg-10 bx--col-md-16 bx--col-sm-16">
              <div id="pricing">
                {/* Section Priing Table*/}
                <div className="bx--row">
                  <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                    <div className="bx--row">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                        <p className="page_body font-weight-600">
                          <FormattedMessage {...messages.page_heading_4} />
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bx--row mar6">
                  <div className="bx--col-lg-10 bx--col-md-16 bx--col-sm-2">
                    <div className="bx--row">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-4">
                        <p className="page_body">
                          <FormattedMessage {...messages.label_1} />
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bx--col-lg-4 bx--col-md-16 bx--col-sm-2">
                    <div className="bx--row">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-4">
                        <p className="page_body float-right fs-20">
                          {preventions}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bx--row mar6">
                  <div className="bx--col-lg-10 bx--col-md-16 bx--col-sm-2">
                    <div className="bx--row">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-4">
                        <p className="page_body">
                          <FormattedMessage {...messages.label_2} />
                        </p>
                      </div>
                    </div>
                  </div>
                  {/* Numbers 8 */}
                  <div className="bx--col-lg-4 bx--col-md-16 bx--col-sm-2">
                    <div className="bx--row">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-4">
                        <p className="page_body float-right fs-20">
                          {speedGain}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bx--row mar6">
                  <div className="bx--col-lg-14 bx--col-md-16 bx--col-sm-16">
                    <hr className="instana-roi__custom-hr" />
                  </div>
                </div>
                <div className="bx--row mar6">
                  <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                    <div className="bx--row">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                        <p className="page_body font-weight-600">
                          <FormattedMessage {...messages.page_heading_5} />
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bx--row mar6">
                  <div className="bx--col-lg-10 bx--col-md-16 bx--col-sm-2">
                    <div className="bx--row">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-4">
                        <p className="page_body">
                          <FormattedMessage {...messages.label_3} />
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bx--col-lg-4 bx--col-md-16 bx--col-sm-2">
                    <div className="bx--row">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-4">
                        <p className="page_body float-right fs-20">
                          {developerEffiencyGains}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bx--row mar6">
                  <div className="bx--col-lg-10 bx--col-md-16 bx--col-sm-2">
                    <div className="bx--row">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-4">
                        <p className="page_body">
                          <FormattedMessage {...messages.label_4} />
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bx--col-lg-4 bx--col-md-16 bx--col-sm-2">
                    <div className="bx--row">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-4">
                        <p className="page_body float-right fs-20">
                          {itOpsEffiencyGains}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bx--row mar6">
                  <div className="bx--col-lg-10 bx--col-md-16 bx--col-sm-2">
                    <div className="bx--row">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-4">
                        <p className="page_body">
                          <FormattedMessage {...messages.label_6} />
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bx--col-lg-4 bx--col-md-16 bx--col-sm-2">
                    <div className="bx--row">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-4">
                        <p className="page_body float-right fs-20">
                          {openSourceGains}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bx--row">
                  <div className="bx--col-lg-10 bx--col-md-16 bx--col-sm-2 bvaresults-total">
                    <div className="bx--row">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-4">
                        <p className="page_body font-weight-600">
                          <FormattedMessage {...messages.page_heading_total} />
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bx--col-lg-4 bx--col-md-16 bx--col-sm-2 bvaresults-total">
                    <div className="bx--row">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-4">
                        <p className="page_body float-right fs-24">
                          {totalEffiencyGains}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* FootNote */}
              <div id="footnote">
                <div className="bx--row mar3">
                  <div className="bx--col-lg-14 bx--col-md-16 bx--col-sm-16">
                    <div className="footnote_text instana-roi__footnote_text">
                      <FormattedMessage {...messages.footnote_2} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="bx--row mt-10">
                <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16 bva-buttons">
                  <ButtonSet className="BVA_Results__stack-button">
                    <Button
                      renderIcon={Calendar16}
                      kind="primary"
                      onClick={() => {
                        bookAMeeting();
                        segmentsTracking('CTA Clicked', {
                          pageTitle: 'Instana ROI Estimator',
                          object: 'BVAResults Page',
                          resultValue: 'CTA clicked',
                          CTA: 'Book a meeting',
                          location: 'Instana ROI Estimator',
                          action: '',
                          field: '',
                        });
                      }}
                    >
                      <FormattedMessage {...messages.book_a_meeting} />
                    </Button>
                    <Button
                      renderIcon={!pdfIcon ? PdfReference16 : null}
                      kind="secondary"
                      onClick={() => {
                        setShowPDF(true);
                        setPdfIcon(true);
                        segmentsTracking('CTA Clicked', {
                          pageTitle: 'Instana ROI Estimator',
                          object: 'BVAResults Page',
                          resultValue: 'CTA clicked',
                          CTA: 'Export to PDF',
                          location: 'Instana ROI Estimator',
                          action: '',
                          field: '',
                        });
                      }}
                      disabled={showPDF}
                    >
                      {pdfIcon ? (
                        <InlineLoading
                          status={loaderStatus}
                          description={loaderDes}
                        />
                      ) : (
                        <FormattedMessage {...messages.export_to_pdf} />
                      )}
                    </Button>
                    <Button
                      renderIcon={Reset16}
                      kind="tertiary"
                      onClick={() => {
                        reset();
                        setViewBVAResults(false);
                        segmentsTracking('CTA Clicked', {
                          pageTitle: 'Instana ROI Estimator',
                          object: 'BVAResults Page',
                          resultValue: 'CTA clicked',
                          CTA: 'Start Over',
                          location: 'Instana ROI Estimator',
                          action: '',
                          field: '',
                        });
                      }}
                    >
                      <FormattedMessage {...messages.start_over} />
                    </Button>
                  </ButtonSet>
                  {pdfExport ? (
                    <div className="pdf">
                      <PDFDownloadLink
                        document={pdfObject}
                        fileName={`instana_bva_${new Date()}.pdf`}
                        onClick={() => {
                          init(true);
                          setPdfExport(false);
                        }}
                      >
                        <p className="MAS_PE__blue-highlight">
                          <u>
                            <FormattedMessage {...messages.downloadText} />
                            <DocumentPdf32 className="load_icon" />
                          </u>
                        </p>
                      </PDFDownloadLink>{' '}
                    </div>
                  ) : null}
                </div>
                <div className="feedback-section">
                  <Feedback
                    pageTitle="Instana ROI Estimator"
                    object="Pricing Page"
                    messages={messages}
                    productTitle={productTitle}
                    pageUrl={pageUrl}
                    emailJson={emailCOSJson}
                  ></Feedback>
                </div>
              </div>
            </div>
            <div
              className="bx--col-lg-6 bx--col-md-16 bx--col-sm-16 mt-07-only-sm bvaresults-charts"
              id="summary"
            >
              {/* Section - Charts & Summary */}
              <div className="bx--row">
                <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                  <div className="bx--row">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <p className="page_body font-weight-600">
                        <FormattedMessage {...messages.charts_heading} />
                      </p>
                    </div>
                  </div>
                  <div className="bx--row mar6">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <p className="page_body">
                        <FormattedMessage {...messages.charts_label_1} />
                      </p>
                    </div>
                  </div>
                  <div className="bx--row mar6">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <LineChart
                        data={chart.data}
                        options={chart.options}
                      ></LineChart>
                    </div>
                  </div>
                  <div className="bx--row mar3">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <hr className="instana-roi__gray-hr" />
                    </div>
                  </div>
                  <div className="bx--row mar6">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <p className="page_body font-weight-600">
                        <FormattedMessage {...messages.chart_heading_summary} />
                      </p>
                    </div>
                  </div>
                  <div className="bx--row mar6">
                    <div className="bx--col-lg-8 bx--col-md-16 bx--col-sm-2">
                      <p className="page_body">
                        <FormattedMessage {...messages.chart_label_summary_1} />
                      </p>
                    </div>
                    <div className="bx--col-lg-8 bx--col-md-16 bx--col-sm-2">
                      <p className="page_body">{disruptions}</p>
                    </div>
                  </div>
                  <div className="bx--row mt-03">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <hr className="instana-roi__gray-hr" />
                    </div>
                  </div>
                  <div className="bx--row mt-03">
                    <div className="bx--col-lg-8 bx--col-md-16 bx--col-sm-2">
                      <p className="page_body">
                        <FormattedMessage {...messages.chart_label_summary_2} />
                      </p>
                    </div>
                    <div className="bx--col-lg-8 bx--col-md-16 bx--col-sm-2">
                      <p className="page_body">{avgTime}</p>
                    </div>
                  </div>
                  <div className="bx--row mt-03">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <hr className="instana-roi__gray-hr" />
                    </div>
                  </div>
                  <div className="bx--row mt-03">
                    <div className="bx--col-lg-8 bx--col-md-16 bx--col-sm-2">
                      <p className="page_body">
                        <FormattedMessage {...messages.chart_label_summary_3} />
                      </p>
                    </div>
                    <div className="bx--col-lg-8 bx--col-md-16 bx--col-sm-2">
                      <p className="page_body">{developers}</p>
                    </div>
                  </div>
                  <div className="bx--row mt-03">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <hr className="instana-roi__gray-hr" />
                    </div>
                  </div>
                  <div className="bx--row mt-03">
                    <div className="bx--col-lg-8 bx--col-md-16 bx--col-sm-2">
                      <p className="page_body">
                        <FormattedMessage {...messages.chart_label_summary_4} />
                      </p>
                    </div>
                    <div className="bx--col-lg-8 bx--col-md-16 bx--col-sm-2">
                      <p className="page_body">{itOpsEmployees}</p>
                    </div>
                  </div>
                  <div className="bx--row mt-03">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <hr className="instana-roi__gray-hr" />
                    </div>
                  </div>
                  <div className="bx--row mt-03">
                    <div className="bx--col-lg-8 bx--col-md-16 bx--col-sm-2">
                      <p className="page_body">
                        <FormattedMessage {...messages.chart_label_summary_5} />
                      </p>
                    </div>
                    <div className="bx--col-lg-8 bx--col-md-16 bx--col-sm-2">
                      <p className="page_body">{openSourceTechnoList}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
